import { Row, SortingFn } from '@tanstack/react-table';
import React, { useMemo } from 'react';
import { AdsTable } from './components/ads-table';
import type { TopAdData } from './hooks/use-social-ads-overview-tab';
import { useSocialAdsTopAdsTable } from './components/ads-table/hooks/use-social-ads-top-ads-table';
import { useAdsGenericTable } from './components/ads-table/hooks/use-ads-generic-table';
import { selectedAdsColumnsToColumnDefInput } from './components/ads-table/utils/selectedColumnsToColumnDef';
import { AdsColumnDefinitionInput } from './components/ads-table/ads-table-column-config';
import { useAdsColumnDialog } from './components/ads-table/hooks/use-ads-column-dialog';
import { AdsColumnDialog } from './components/ads-table/ads-column-dialog';
import { sharedRenderRow } from './components/ads-table/shared-render-row';

// Stable async no-op function to prevent unnecessary re-renders
const defaultOnSaveColumns = async (): Promise<void> => {};

// Map AdsRowType to sharedRenderRow rowType strings
const mapRowTypeToAccessorKey = (rowType: AdsRowType, accessorKey: string): string => {
  switch (rowType) {
    case ADS_ROW_TYPE.AD_NAME:
      return 'by.name';
    case ADS_ROW_TYPE.SOCIAL_ACCOUNT:
      return 'by.id';
    case ADS_ROW_TYPE.ROAS:
      return 'metrics.roas';
    case ADS_ROW_TYPE.CURRENCY:
    case ADS_ROW_TYPE.PERCENTAGE:
    case ADS_ROW_TYPE.NUMBER:
      return accessorKey;
    default:
      return accessorKey;
  }
};

// Row type map for different data types
export const ADS_ROW_TYPE = {
  DEFAULT: 'DEFAULT',
  AD_NAME: 'AD_NAME',
  SOCIAL_ACCOUNT: 'SOCIAL_ACCOUNT',
  CURRENCY: 'CURRENCY',
  PERCENTAGE: 'PERCENTAGE',
  NUMBER: 'NUMBER',
  ROAS: 'ROAS',
} as const;

export type AdsRowType = (typeof ADS_ROW_TYPE)[keyof typeof ADS_ROW_TYPE];

// Mapped column interface similar to Analytics table
export interface AdsMappedColumn {
  id: string;
  enableSorting: boolean;
  enableGlobalFilter: boolean;
  sortingFn: SortingFn<TopAdData>;
  meta: {
    greyBackground?: true;
    headerGroupBorder: {
      borderColor?: string;
    };
    onToggleClick: (() => void) | null;
    isColumnExpanded?: boolean;
    isExpandableColumn?: boolean;
    draggable?: boolean;
  };
  header: string;
  rowType: AdsRowType;
  size?: number;
  minSize?: number;
  maxSize?: number;
  accessorFn?: (row: TopAdData) => unknown;
  isSubColumn?: boolean;
}

interface SocialAdsTopAdsProps {
  isLoading?: boolean;
  isRefreshing?: boolean;
  data: TopAdData[];
  selectedColumns: AdsColumnDefinitionInput[];
  setSelectedColumns: (
    columns: AdsColumnDefinitionInput[] | ((prev: AdsColumnDefinitionInput[]) => AdsColumnDefinitionInput[]),
  ) => void;
  onSaveColumns?: (columns: string[]) => Promise<void>;
}

const SocialAdsTopAds: React.FC<SocialAdsTopAdsProps> = ({
  isLoading = false,
  isRefreshing = false,
  data,
  selectedColumns,
  setSelectedColumns,
  onSaveColumns,
}) => {
  const { adsTableProps } = useSocialAdsTopAdsTable({ data, isLoading, isRefreshing });

  // Use the generic table hook for column ordering
  const { tableProps } = useAdsGenericTable({
    selectedColumns,
    setSelectedColumns,
  });

  // Use the column dialog hook
  const { columnDialogProps, openDialog } = useAdsColumnDialog({
    selectedColumns,
    onSave: onSaveColumns || defaultOnSaveColumns,
  });

  // Map selected columns to column definitions
  const mappedColumns = useMemo(
    () => selectedAdsColumnsToColumnDefInput(selectedColumns, tableProps.handleExpandColumn),
    [selectedColumns, tableProps.handleExpandColumn],
  );

  const columns = useMemo(() => {
    return mappedColumns.map((data: AdsMappedColumn) => {
      return {
        ...data,
        accessorKey: data.id,
        header: () => {
          return <div className="font-medium text-gray-900 text-[14px]">{data.header}</div>;
        },
        cell: ({ row }: { row: Row<TopAdData> }) => {
          if (isRefreshing) {
            return <div className="h-4 rounded bg-gray-100 animate-pulse w-full" />;
          }
          const mappedAccessorKey = mapRowTypeToAccessorKey(data.rowType, data.id);
          return sharedRenderRow(data.rowType, row, { ...data, id: mappedAccessorKey });
        },
      };
    });
  }, [isRefreshing, mappedColumns]);

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 min-h-0">
        <AdsTable
          {...adsTableProps}
          columns={columns}
          draggable
          onColumnDragEnd={tableProps.handleDragEnd}
          onOpenColumnDialog={openDialog}
        />
      </div>

      {/* Column Dialog */}
      <AdsColumnDialog {...columnDialogProps} />
    </div>
  );
};

export default SocialAdsTopAds;
